/* eslint-disable @typescript-eslint/no-explicit-any */
import { useSessionUpdateContext } from '../../context/SessionUpdateContext';
import { useSessionUpdateZustandStore } from '../../modules/store/sessionUpdateZustandStore';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { FEATURE_FLAGS, FLAG_TYPE } from '@marriott/mi-feature-flag-utils';
import { SelectedInteractionsContextType } from '../../context/SessionUpdateContext/index.types';

/**
 * Hook that conditionally returns data from either the Zustand store or React Context
 * based on feature flags, maintaining the same API surface for seamless migration
 *
 * Uses the same feature flag as PageContext migration: EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND
 *
 * Note: The Zustand store is initialized proactively in _app.tsx where
 * the SessionUpdateContext.Provider is set up, rather than reactively in this hook.
 * This is more efficient and follows the principle of initializing at the source.
 */
export const useConditionalSessionUpdateContext = (): SelectedInteractionsContextType => {
  // Get feature flags from the client environment store
  const featureFlags = useClientEnvVarsStore(state => state.envVarsObject['featureFlags']) as unknown as Record<
    string,
    FLAG_TYPE
  >;

  // Use the same feature flag as PageContext migration for consistency
  const shouldUseZustand = featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND] ?? false;

  // Always call hooks at top level (Rules of Hooks)
  const sessionUpdateContext = useSessionUpdateContext();
  const zustandStore = useSessionUpdateZustandStore();

  if (shouldUseZustand) {
    // Return Zustand store data (initialized proactively in _app.tsx)
    return {
      interactionsSelected: zustandStore.interactionsSelected,
      setInteractionsSelected: zustandStore.setInteractionsSelected,
    };
  } else {
    // Return React Context data
    return {
      interactionsSelected: sessionUpdateContext?.interactionsSelected || {
        availability: false,
        price: false,
        points: false,
        amenities: false,
        brands: false,
        distance: false,
        transportation: false,
        hotelType: false,
        events: false,
        activities: false,
        states: false,
        city: false,
        pagination: false,
        taxesAndFees: false,
        currencyConversion: false,
        sortBy: false,
        quickFilters: false,
        savedProperties: false,
        listOrMapButton: false,
      },
      setInteractionsSelected: sessionUpdateContext?.setInteractionsSelected || (() => {}),
    };
  }
};

/**
 * Advanced hook that provides additional store methods when using Zustand
 * This is useful for components that want to take advantage of the improved store API
 * Only use this if you need the additional methods that don't exist in the original context
 */
export const useConditionalSessionUpdateContextAdvanced = () => {
  const basicContext = useConditionalSessionUpdateContext();

  // Get feature flags to determine if we should expose advanced methods
  const featureFlags = useClientEnvVarsStore(state => state.envVarsObject['featureFlags']) as unknown as Record<
    string,
    FLAG_TYPE
  >;

  const shouldUseZustand = featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND] ?? false;

  // Always call hooks at top level
  const zustandStore = useSessionUpdateZustandStore();

  if (shouldUseZustand) {
    return {
      ...basicContext,
      // Additional methods available only when using Zustand
      setInteractionSelected: zustandStore.setInteractionSelected,
      resetInteractions: zustandStore.resetInteractions,
      isUsingZustand: true,
    };
  } else {
    return {
      ...basicContext,
      // Provide no-op implementations for advanced methods when using context
      setInteractionSelected: () => {},
      resetInteractions: () => {},
      isUsingZustand: false,
    };
  }
};
